import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Select, Button, Spin, message, Alert } from "antd";
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  TeamOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { usePatientData } from "../../../hooks/usePatientData";
import {
  useGetExternalConsultants,
  useCreateExternalConsultant,
} from "../../../hooks/useExternal";

const { Option } = Select;

const PatientInfoModal = ({
  visible,
  onCancel,
  booking,
  onActivate,
  isMobile,
  patientId,
  fetchPatients
  ,updateOnly,
  onUpdate

}) => {
  const [form] = Form.useForm();
  const [consultantForm] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isConsultantModalVisible, setIsConsultantModalVisible] =
    useState(false);

  const { patientInfo, loading, error, updatePatientInfo } = usePatientData(
    patientId,
  );

  // Fetch external consultants
  const { data: consultants = [], isLoading: consultantsLoading } =
    useGetExternalConsultants();
  const createConsultantMutation = useCreateExternalConsultant();

  useEffect(() => {
    if (visible && patientInfo) {
      const birth = patientInfo.birthDay
        ? new Date(patientInfo.birthDay)
        : null;

      form.setFieldsValue({
        firstName: patientInfo.firstName || "",
        lastName: patientInfo.lastName || "",
        email: patientInfo.email || "",
        phoneNumber: patientInfo.phoneNumber || "",
        gender:
          patientInfo.gender === "Female"
            ? 1
            : patientInfo.gender === "Male"
              ? 0
              : undefined,
        externalConsultantId: patientInfo.externalConsultantId || undefined,
        // Parse "1999-04-03" into separate fields
        dobDay: birth ? birth.getUTCDate() : undefined,
        dobMonth: birth ? birth.getUTCMonth() + 1 : undefined,
        dobYear: birth ? birth.getUTCFullYear() : undefined,
      });
      setHasChanges(false);
    }
  }, [visible, patientInfo, form]);

  // Check if patient info is complete
  const isPatientInfoComplete = () => {
    if (!patientInfo) return false;

    return !!(
      patientInfo.firstName?.trim() &&
      patientInfo.lastName?.trim() &&
      patientInfo.email?.trim() &&
      patientInfo.phoneNumber?.trim() &&
      (patientInfo.gender === 0 || patientInfo.gender === 1)
    );
  };

  const handleFormChange = () => {
    setHasChanges(true);
  };

  const handleUpdateAndActivate = async () => {
    try {
      const values = await form.validateFields();
      setIsSubmitting(true);

      // Update patient info if there are changes
      if (hasChanges) {
        await updatePatientInfo({
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          phoneNumber: values.phoneNumber,
          gender: values.gender,
          externalConsultantId: values.externalConsultantId || null,
          birthDay: `${values.dobYear}-${String(values.dobMonth).padStart(2, "0")}-${String(values.dobDay).padStart(2, "0")}`,
        });
      }
    await  fetchPatients?.()
      if (onActivate &&!updateOnly) {
        await onActivate(booking);
      } else {
        onCancel();

      }

      // Close modal
      onCancel();
      form.resetFields();
      onUpdate()
      setHasChanges(false);
    } catch (error) {
      console.error(
        "Error updating patient info or activating booking:",
        error,
      );
      if (error.errorFields) {
        message.error("Please fill in all required fields");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setHasChanges(false);
    onCancel();
  };

  // Handle Add Consultant Modal
  const handleAddConsultant = () => {
    consultantForm.resetFields();
    setIsConsultantModalVisible(true);
  };

  const handleConsultantSubmit = async () => {
    try {
      const values = await consultantForm.validateFields();

      const result = await createConsultantMutation.mutateAsync(values);

      // Set the newly created consultant as selected
      if (result && result.id) {
        form.setFieldsValue({ externalConsultantId: result.id });
        setHasChanges(true);
      }

      setIsConsultantModalVisible(false);
      consultantForm.resetFields();
      message.success("Consultant added successfully");
    } catch (err) {
      console.error("Validation failed:", err);
      if (!err.errorFields) {
        message.error("Failed to add consultant");
      }
    }
  };

  const isComplete = isPatientInfoComplete();

  return (
    <>
      <Modal
        title={
          <div className="flex items-center">
            <div
              className={`${isMobile ? "w-6 h-6" : "w-8 h-8"} bg-primary rounded-full flex items-center justify-center ${isMobile ? "mr-2" : "mr-3"}`}
            >
              <UserOutlined
                className={`text-white ${isMobile ? "text-xs" : "text-sm"}`}
              />
            </div>
            <span className={isMobile ? "text-sm" : ""}>
              Verify Patient Information
            </span>
          </div>
        }
        open={visible}
        onCancel={handleCancel}
        width={isMobile ? "95vw" : 800}
        centered={isMobile}
        footer={[
          <Button
            key="cancel"
            onClick={handleCancel}
            size={isMobile ? "middle" : "large"}
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={isSubmitting}
            onClick={handleUpdateAndActivate}
            size={isMobile ? "middle" : "large"}
            className="bg-primary border-none"
            icon={<CheckCircleOutlined />}
          >
            {booking  ? "Update & Activate" : "Update"}
          </Button>,
        ]}
      >
        <Spin spinning={loading || consultantsLoading}>
          <div className="mb-4">
            {/* Status Alert */}
            {/* {!loading && !isComplete && (
              <Alert
                message="Incomplete Patient Information"
                description="Please complete all required fields before activating this booking."
                type="warning"
                icon={<ExclamationCircleOutlined />}
                showIcon
                className="mb-4"
              />
            )} */}

            {!loading && isComplete && !hasChanges && (
              <Alert
                message="Patient Information Complete"
                description="All required information is filled. You can activate the booking."
                type="success"
                icon={<CheckCircleOutlined />}
                showIcon
                className="mb-4"
              />
            )}

            {/* Patient Info */}
            {booking && (
              <div
                className={`${isMobile ? "p-3" : "p-4"} bg-blue-50 rounded-xl border border-blue-200 mb-4`}
              >
                <div
                  className={`font-semibold text-blue-800 ${isMobile ? "text-sm" : ""}`}
                >
                  Booking for: {booking.patientName}
                </div>
                <div
                  className={`text-blue-600 ${isMobile ? "text-xs" : "text-sm"}`}
                >
                  Clinic: {booking.clinicName}
                </div>
              </div>
            )}
          </div>

          {/* Form */}
          <Form form={form} layout="vertical" onValuesChange={handleFormChange}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Item
                name="firstName"
                label={
                  <span className={isMobile ? "text-sm" : ""}>First Name</span>
                }
                rules={[
                  { required: true, message: "Please enter first name" },
                  {
                    min: 2,
                    message: "First name must be at least 2 characters",
                  },
                ]}
              >
                <Input
                  prefix={<UserOutlined className="text-gray-400" />}
                  placeholder="Enter first name"
                  size={isMobile ? "middle" : "large"}
                  className="rounded-lg"
                />
              </Form.Item>

              <Form.Item
                name="lastName"
                label={
                  <span className={isMobile ? "text-sm" : ""}>Last Name</span>
                }
                rules={[
                  { required: true, message: "Please enter last name" },
                  {
                    min: 2,
                    message: "Last name must be at least 2 characters",
                  },
                ]}
              >
                <Input
                  prefix={<UserOutlined className="text-gray-400" />}
                  placeholder="Enter last name"
                  size={isMobile ? "middle" : "large"}
                  className="rounded-lg"
                />
              </Form.Item>
            </div>

            <div className="flex mb-1 gap-4">
              <Form.Item
                name="dobDay"
                label="Date Of Birth"
                className="flex-1 mb-0"
                rules={[{ required: true, message: "Day is required" }]}
              >
                <Select placeholder="Day">
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                    <Select.Option key={day} value={day}>
                      {day}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="dobMonth"
                label=" "
                className="flex-1 mb-0"
                rules={[{ required: true, message: "Month is required" }]}
              >
                <Select placeholder="Month">
                  {[
                    "January",
                    "February",
                    "March",
                    "April",
                    "May",
                    "June",
                    "July",
                    "August",
                    "September",
                    "October",
                    "November",
                    "December",
                  ].map((month, index) => (
                    <Select.Option key={index + 1} value={index + 1}>
                      {month}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="dobYear"
                label=" "
                className="flex-1 mb-0"
                rules={[{ required: true, message: "Year is required" }]}
              >
                <Select placeholder="Year" showSearch>
                  {Array.from(
                    { length: 100 },
                    (_, i) => new Date().getFullYear() - i,
                  ).map((year) => (
                    <Select.Option key={year} value={year}>
                      {year}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
            <Form.Item
              name="phoneNumber"
              label={
                <span className={isMobile ? "text-sm" : ""}>Phone Number</span>
              }
              rules={[
                { required: true, message: "Please enter phone number" },
                { min: 10, message: "Phone number must be at least 10 digits" },
              ]}
            >
              <Input
                prefix={<PhoneOutlined className="text-gray-400" />}
                placeholder="Enter phone number"
                size={isMobile ? "middle" : "large"}
                className="rounded-lg"
              />
            </Form.Item>

            <div className="grid grid-cols-1 justify-between gap-2  items-center ">
              <Form.Item
                name="gender"
                label={
                  <span className={isMobile ? "text-sm" : ""}>Gender</span>
                }
                rules={[{ required: true, message: "Please select gender" }]}
              >
                <Select
                  placeholder="Select gender"
                  size={isMobile ? "middle" : "large"}
                  className="rounded-lg"
                >
                  <Option value={0}>Male</Option>
                  <Option value={1}>Female</Option>
                </Select>
              </Form.Item>
           
            
            </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 justify-between gap-2  items-center ">
                            <Form.Item
                name="externalConsultantId"
                label="Select consultant (optional)"
              >
                <Select
                  placeholder="Select consultant"
                  size={isMobile ? "middle" : "large"}
                  className="rounded-lg"
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  suffixIcon={<TeamOutlined className="text-gray-400" />}
                  loading={consultantsLoading}
                >
                  {consultants.map((consultant) => (
                    <Option key={consultant.id} value={consultant.id}>
                      {consultant.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Button
                type="link"
                icon={<PlusOutlined />}
                onClick={handleAddConsultant}
                className="p-0 text-primary hover:text-primary-dark justify-self-start"
                size="small"
              >
                Add New
              </Button></div>

          </Form>
        </Spin>
      </Modal>

      {/* Add Consultant Modal */}
      <Modal
        title={
          <div className="flex items-center gap-3 text-xl">
            <UserOutlined className="text-primary" />
            <span className="font-bold">Add New External Consultant</span>
          </div>
        }
        open={isConsultantModalVisible}
        onCancel={() => {
          setIsConsultantModalVisible(false);
          consultantForm.resetFields();
        }}
        onOk={handleConsultantSubmit}
        confirmLoading={createConsultantMutation.isPending}
        okText="Create Consultant"
        cancelText="Cancel"
        okButtonProps={{
          className:
            "bg-gradient-to-r from-primary to-primary_hover border-0 shadow-md hover:shadow-lg",
        }}
        width={600}
        className="rounded-2xl"
      >
        <Form form={consultantForm} layout="vertical" className="mt-6">
          <Form.Item
            name="name"
            label={
              <span className="font-semibold text-base">Consultant Name</span>
            }
            rules={[
              { required: true, message: "Please enter consultant name" },
            ]}
          >
            <Input
              placeholder="Enter consultant full name"
              size="large"
              prefix={<UserOutlined className="text-gray-400" />}
              className="rounded-xl"
            />
          </Form.Item>
          <Form.Item
            name="email"
            label={
              <span className="font-semibold text-base">Email Address</span>
            }
            rules={[
              { required: true, message: "Please enter email address" },
              { type: "email", message: "Please enter a valid email address" },
            ]}
          >
            <Input
              placeholder="Enter email address"
              size="large"
              prefix={<MailOutlined className="text-gray-400" />}
              className="rounded-xl"
            />
          </Form.Item>
          <Form.Item
            name="phone"
            label={
              <span className="font-semibold text-base">Phone Number</span>
            }
            rules={[
              { required: true, message: "Please enter phone number" },
              {
                pattern: /^[0-9+\-\s()]+$/,
                message: "Please enter a valid phone number",
              },
            ]}
          >
            <Input
              placeholder="Enter phone number"
              size="large"
              prefix={<PhoneOutlined className="text-gray-400" />}
              className="rounded-xl"
            />
          </Form.Item>
          <Form.Item
            name="about"
            label={
              <span className="font-semibold text-base">About (Optional)</span>
            }
          >
            <Input.TextArea
              placeholder="Enter additional information about the consultant"
              rows={4}
              className="rounded-xl"
            />
          </Form.Item>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Date Of Birth
          </label>
          <div className="grid grid-cols-3 gap-4">
            <Form.Item
              name="dobDay"
              label="Day"
              rules={[{ required: true, message: "Day is required" }]}
            >
              <Select placeholder="Day">
                {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                  <Option key={day} value={day}>
                    {day}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="dobMonth"
              label="Month"
              rules={[{ required: true, message: "Month is required" }]}
            >
              <Select placeholder="Month">
                {[
                  "January",
                  "February",
                  "March",
                  "April",
                  "May",
                  "June",
                  "July",
                  "August",
                  "September",
                  "October",
                  "November",
                  "December",
                ].map((month, index) => (
                  <Option key={index + 1} value={index + 1}>
                    {month}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="dobYear"
              label="Year"
              rules={[{ required: true, message: "Year is required" }]}
            >
              <Select placeholder="Year" showSearch>
                {Array.from(
                  { length: 100 },
                  (_, i) => new Date().getFullYear() - i,
                ).map((year) => (
                  <Option key={year} value={year}>
                    {year}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default PatientInfoModal;
